import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { getGuests, getGuest, createGuest, updateGuest, deleteGuest, getRooms, getProperties, giveNotice, withdrawNotice, reverseLeave, processLeave, shiftGuest, recordPayment, changeDueDate, joinBookedGuest, recordAdvanceRent, exportGuestList, getGuestAdjustments, getPenaltySuggestion, createAdjustment, updateAdjustment, deleteAdjustment } from '../lib/api';
import { formatDate, formatDateObj, getISTDateString } from '../lib/dateUtils';
import { GuestKYC } from '../components/GuestKYC';
import { toast } from 'sonner';
import { Users, Plus, Eye, Clock, LogOut, ArrowRightLeft, RotateCcw, Phone, Pencil, Banknote, Save, X, Trash2, Calendar, FileText, Download, CheckCircle, Search, AlertTriangle, Gift } from 'lucide-react';
import { IconTip } from '../components/IconTip';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarPicker } from '../components/ui/calendar';
import { format as formatFns } from 'date-fns';

// Helper to extract error message from API response (handles Pydantic validation errors)
const getErrorMessage = (err, fallback = 'An error occurred') => {
  const detail = err.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    // Pydantic validation error format: [{type, loc, msg, input, url}, ...]
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  return fallback;
};

const StatusBadge = ({ status, displayType, isLeavingToday, overstayDays }) => {
  // Use displayType for more meaningful status
  const type = displayType || status;
  
  const variants = {
    booked: 'bg-blue-100 text-blue-700',
    new_join: 'bg-emerald-100 text-emerald-700',
    occupied: 'bg-slate-100 text-slate-700',
    notice: 'bg-amber-100 text-amber-700',
    shifting: 'bg-purple-100 text-purple-700',
    left: 'bg-rose-100 text-rose-700',
    joined: 'bg-emerald-100 text-emerald-700',
    day_basis: 'bg-purple-100 text-purple-700',
    overstay: 'bg-red-500 text-white'  // Overstay - RED and prominent
  };

  const labels = {
    booked: 'Booked',
    new_join: 'Joined',
    occupied: 'Occupied',
    notice: 'Notice',
    shifting: 'Shifting',
    left: 'Left',
    joined: 'Joined',
    day_basis: 'Day Basis',
    overstay: 'Overstay'
  };

  return (
    <div className="flex flex-col gap-1">
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${variants[type] || 'bg-slate-100 text-slate-700'}`}>
        {labels[type] || type}
        {type === 'overstay' && overstayDays > 0 && ` (${overstayDays}d)`}
      </span>
      {isLeavingToday && (
        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-orange-500 text-white animate-pulse">
          ⚠️ LEAVING TODAY
        </span>
      )}
    </div>
  );
};

export const Guests = () => {
  const { canAdd, canEdit, canDelete, canExport } = useAuth();
  const [guests, setGuests] = useState([]);
  const [properties, setProperties] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperty, setSelectedProperty] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [onlyDues, setOnlyDues] = useState(false);
  const [joinRange, setJoinRange] = useState(undefined);
  const [joinPopoverOpen, setJoinPopoverOpen] = useState(false);
  const [dueDayFilter, setDueDayFilter] = useState('all');
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [noticeDialogOpen, setNoticeDialogOpen] = useState(false);
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [convertJoinedDialogOpen, setConvertJoinedDialogOpen] = useState(false);
  const [cancelBookingDialogOpen, setCancelBookingDialogOpen] = useState(false);
  
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [guestDetails, setGuestDetails] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  
  // Form states
  const [form, setForm] = useState({
    name: '', mobile: '', property_id: '', room_id: '', bed_no: 1,
    booking_date: '', joining_date: '', status: 'joined',
    booking_type: 'monthly', // 'monthly' or 'day_basis'
    num_days: 1, daily_rate: '',
    monthly_rent: '', deposit: '', emergency_contact: ''
  });
  const [noticeDate, setNoticeDate] = useState('');
  const [expectedLeaveDate, setExpectedLeaveDate] = useState('');
  const [useAdvanceNotice, setUseAdvanceNotice] = useState(false);
  const [leaveForm, setLeaveForm] = useState({ leave_date: '', immediate: false });
  const [shiftForm, setShiftForm] = useState({ new_room_id: '', new_bed_no: 1, shift_date: new Date().toISOString().split('T')[0] });
  const [paymentForm, setPaymentForm] = useState({
    guest_type: 'occupied',
    cash_rent: 0,
    cash_deposit: 0,
    cash_electricity: 0,
    cash_penalty: 0,
    bank_rent: 0,
    bank_deposit: 0,
    bank_electricity: 0,
    bank_penalty: 0,
    remarks: '',
    payment_date: getISTDateString()
  });
  // Penalty / Waiver adjustments (shown inside Collect Receipt dialog)
  const [adjustments, setAdjustments] = useState([]);
  const [adjSummary, setAdjSummary] = useState({ penalty_due: 0, penalty_charged: 0, penalty_paid: 0 });
  const [penaltyRate, setPenaltyRate] = useState(100);
  const [showPenaltyForm, setShowPenaltyForm] = useState(false);
  const [showWaiverForm, setShowWaiverForm] = useState(false);
  const [penaltyForm, setPenaltyForm] = useState({ amount: 0, reason: '', month_year: '', days_charged: 0, per_day_rate: 100 });
  const [waiverForm, setWaiverForm] = useState({ component: 'rent', amount: 0, reason: '' });
  const [editingAdjId, setEditingAdjId] = useState(null);
  const [editAdjForm, setEditAdjForm] = useState({ amount: 0, reason: '' });
  const [advanceRentForm, setAdvanceRentForm] = useState({
    months: 3,
    payment_mode: 'cash'
  });
  const [advanceRentDialogOpen, setAdvanceRentDialogOpen] = useState(false);
  const [settleDuesDialogOpen, setSettleDuesDialogOpen] = useState(false);
  const [settleDuesForm, setSettleDuesForm] = useState({
    settlement_type: 'write_off', // 'write_off' or 'adjust'
    remarks: ''
  });
  const [convertJoinedForm, setConvertJoinedForm] = useState({ joining_date: '' });
  const [cancelBookingForm, setCancelBookingForm] = useState({ cancellation_date: '', reason: '' });
  
  const [availableBeds, setAvailableBeds] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [highlightGuestId, setHighlightGuestId] = useState(null);

  useEffect(() => {
    fetchData();
  }, [selectedProperty, selectedStatus]);

  // Handle cross-tab deep-links from Rooms tab (run once on mount)
  useEffect(() => {
    const action = searchParams.get('action');
    const propertyId = searchParams.get('property_id');
    const roomId = searchParams.get('room_id');
    const bedNo = searchParams.get('bed_no');
    const guestId = searchParams.get('guest_id');

    if (action === 'add' && roomId) {
      setForm(f => ({
        ...f,
        property_id: propertyId || '',
        room_id: roomId,
        bed_no: bedNo ? parseInt(bedNo) : 1,
      }));
      setAddDialogOpen(true);
      setSearchParams({}, { replace: true });
    } else if (guestId) {
      // Reset filter so the target guest is never hidden
      setSelectedStatus('all');
      setHighlightGuestId(guestId);
      setSearchParams({}, { replace: true });
    } else if (searchParams.get('status')) {
      setSelectedStatus(searchParams.get('status'));
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll to & temporarily highlight the linked guest row
  useEffect(() => {
    if (highlightGuestId && guests.length) {
      const el = document.querySelector(`[data-testid="guest-row-${highlightGuestId}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      const t = setTimeout(() => setHighlightGuestId(null), 3000);
      return () => clearTimeout(t);
    }
  }, [highlightGuestId, guests]);

  useEffect(() => {
    if (form.room_id) {
      const room = rooms.find(r => r.id === form.room_id);
      if (room) {
        const vacant = room.beds.filter(b => b.status === 'vacant').map(b => b.bed_no);
        setAvailableBeds(vacant);
        if (vacant.length > 0 && !vacant.includes(form.bed_no)) {
          setForm(f => ({ ...f, bed_no: vacant[0] }));
        }
      }
    }
  }, [form.room_id, rooms]);

  // Auto-fill Rent & Deposit from the selected bed (falls back to room default).
  useEffect(() => {
    if (form.room_id && form.bed_no) {
      const room = rooms.find(r => r.id === form.room_id);
      if (room) {
        const bed = (room.beds || []).find(b => b.bed_no === form.bed_no);
        const rent = bed && bed.rent != null ? bed.rent : room.rent_per_bed;
        const dep = bed && bed.deposit != null ? bed.deposit : room.deposit_per_bed;
        setForm(f => ({ ...f, monthly_rent: rent ?? '', deposit: dep ?? '' }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.room_id, form.bed_no, rooms]);

  const fetchData = async () => {
    try {
      const params = {};
      if (selectedProperty !== 'all') params.property_id = selectedProperty;
      // For overstay filter, fetch notice guests (overstay is a subset of notice)
      if (selectedStatus !== 'all' && selectedStatus !== 'overstay') {
        params.status = selectedStatus;
      } else if (selectedStatus === 'overstay') {
        params.status = 'notice'; // Fetch notice, filter overstay on frontend
      }
      
      const [guestsRes, propsRes, roomsRes] = await Promise.all([
        getGuests(params),
        getProperties(),
        getRooms()
      ]);
      setGuests(guestsRes.data);
      setProperties(propsRes.data);
      // Sort rooms by combined_number (alphanumeric sorting)
      const sortedRooms = [...roomsRes.data].sort((a, b) => {
        const aNum = a.combined_number || '';
        const bNum = b.combined_number || '';
        return aNum.localeCompare(bNum, undefined, { numeric: true, sensitivity: 'base' });
      });
      setRooms(sortedRooms);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGuest = async (e) => {
    e.preventDefault();
    try {
      // Validate mobile number
      const mobileDigits = form.mobile.replace(/\D/g, '');
      if (mobileDigits.length !== 10) {
        toast.error('Mobile number must be exactly 10 digits');
        return;
      }

      // Validate emergency contact - compulsory, exactly 10 digits
      const emergencyDigits = (form.emergency_contact || '').replace(/\D/g, '');
      if (emergencyDigits.length !== 10) {
        toast.error('Emergency mobile must be exactly 10 digits');
        return;
      }
      
      // Ensure bed_no is a valid number
      const bedNo = parseInt(form.bed_no) || 1;
      
      // Build guest data with only required fields
      const guestData = { 
        name: form.name,
        mobile: mobileDigits,
        emergency_contact: emergencyDigits,
        property_id: form.property_id,
        room_id: form.room_id,
        bed_no: bedNo,
        status: form.status
      };
      
      // Add joining_date only for joined status
      if (form.status === 'joined' && form.joining_date) {
        guestData.joining_date = form.joining_date;
      }

      // Rent & Deposit entered in the form (monthly/joined). Also updates the bed master.
      if (form.status === 'joined') {
        if (form.monthly_rent !== '' && form.monthly_rent != null) {
          guestData.monthly_rent = parseFloat(form.monthly_rent) || 0;
        }
        if (form.deposit !== '' && form.deposit != null) {
          guestData.deposit = parseFloat(form.deposit) || 0;
        }
      }
      
      // Handle day_basis specific fields
      if (form.status === 'day_basis') {
        const dailyRate = parseFloat(form.daily_rate) || 0;
        const numDays = parseInt(form.num_days) || 1;
        guestData.monthly_rent = numDays * dailyRate;
        guestData.booking_type = 'day_basis';
        guestData.num_days = numDays;
        guestData.daily_rate = dailyRate;
        guestData.status = 'day_basis';
        if (form.joining_date) {
          guestData.joining_date = form.joining_date;
        }
      }
      
      await createGuest(guestData);
      toast.success('Guest added successfully');
      setAddDialogOpen(false);
      setForm({ name: '', mobile: '', property_id: '', room_id: '', bed_no: 1, booking_date: '', joining_date: '', status: 'joined', booking_type: 'monthly', num_days: 1, daily_rate: '', monthly_rent: '', deposit: '', emergency_contact: '' });
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to add guest'));
    }
  };

  const handleViewGuest = async (guest) => {
    try {
      const res = await getGuest(guest.id);
      setGuestDetails(res.data);
      setEditForm({
        name: res.data.name,
        mobile: res.data.mobile,
        emergency_contact: res.data.emergency_contact || '',
        deposit_paid: res.data.deposit_paid,
        deposit_due: res.data.deposit_due,
        advance_rent_paid: res.data.advance_rent_paid,
        advance_rent_due: res.data.advance_rent_due,
        monthly_rent: res.data.monthly_rent,
        joining_date: res.data.joining_date,
        electricity_paid: res.data.electricity_paid || 0,
        electricity_due: res.data.electricity_due || 0,
        rent_from_current_cycle: false
      });
      setIsEditing(false);
      setViewDialogOpen(true);
    } catch (err) {
      toast.error('Failed to load guest details');
    }
  };

  const handleSaveEdit = async () => {
    try {
      // Validate mobile number
      const mobileDigits = (editForm.mobile || '').replace(/\D/g, '');
      if (mobileDigits.length !== 10) {
        toast.error('Mobile number must be exactly 10 digits');
        return;
      }

      // Validate emergency contact - must be exactly 10 digits
      const emergencyDigits = (editForm.emergency_contact || '').replace(/\D/g, '');
      if (emergencyDigits.length !== 10) {
        toast.error('Emergency mobile must be exactly 10 digits');
        return;
      }
      
      await updateGuest(guestDetails.id, { ...editForm, mobile: mobileDigits, emergency_contact: emergencyDigits });
      toast.success('Guest updated successfully');
      // Refresh guest details
      const res = await getGuest(guestDetails.id);
      setGuestDetails(res.data);
      setIsEditing(false);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update guest'));
    }
  };

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    try {
      // Calculate entries from form - similar to Payments.js
      const entries = [];
      if (paymentForm.cash_rent > 0) entries.push({ type: 'rent', mode: 'cash', amount: paymentForm.cash_rent });
      if (paymentForm.cash_deposit > 0) entries.push({ type: 'deposit', mode: 'cash', amount: paymentForm.cash_deposit });
      if (paymentForm.cash_electricity > 0) entries.push({ type: 'electricity', mode: 'cash', amount: paymentForm.cash_electricity });
      if (paymentForm.cash_penalty > 0) entries.push({ type: 'penalty', mode: 'cash', amount: paymentForm.cash_penalty });
      if (paymentForm.bank_rent > 0) entries.push({ type: 'rent', mode: 'bank', amount: paymentForm.bank_rent });
      if (paymentForm.bank_deposit > 0) entries.push({ type: 'deposit', mode: 'bank', amount: paymentForm.bank_deposit });
      if (paymentForm.bank_electricity > 0) entries.push({ type: 'electricity', mode: 'bank', amount: paymentForm.bank_electricity });
      if (paymentForm.bank_penalty > 0) entries.push({ type: 'penalty', mode: 'bank', amount: paymentForm.bank_penalty });

      if (entries.length === 0) {
        toast.error('Please enter at least one amount');
        return;
      }

      // Record each payment separately
      for (const entry of entries) {
        await recordPayment({
          guest_id: guestDetails.id,
          amount: entry.amount,
          payment_type: entry.type,
          payment_mode: entry.mode,
          remarks: paymentForm.remarks,
          payment_date: paymentForm.payment_date
        });
      }
      
      toast.success(`${entries.length} payment(s) recorded successfully`);
      setPaymentDialogOpen(false);
      setPaymentForm({ 
        guest_type: 'occupied',
        cash_rent: 0, 
        cash_deposit: 0, 
        cash_electricity: 0,
        cash_penalty: 0,
        bank_rent: 0,
        bank_deposit: 0,
        bank_electricity: 0,
        bank_penalty: 0,
        remarks: '', 
        payment_date: getISTDateString()
      });
      // Refresh guest details
      const guestRes = await getGuest(guestDetails.id);
      setGuestDetails(guestRes.data);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to record payment'));
    }
  };

  // ===== Penalty / Waiver handlers (inside Collect Receipt dialog) =====
  const loadAdjustments = async (guestId) => {
    try {
      const res = await getGuestAdjustments(guestId);
      setAdjustments(res.data.adjustments || []);
      setAdjSummary({
        penalty_due: res.data.penalty_due || 0,
        penalty_charged: res.data.penalty_charged || 0,
        penalty_paid: res.data.penalty_paid || 0,
      });
    } catch (err) {
      setAdjustments([]);
    }
  };

  const openPaymentDialog = async () => {
    setShowPenaltyForm(false);
    setShowWaiverForm(false);
    setEditingAdjId(null);
    setPaymentDialogOpen(true);
    if (guestDetails?.id) loadAdjustments(guestDetails.id);
  };

  // Open Collect Receipt directly from the Guests list (without opening details)
  const openCollectForGuest = async (guest) => {
    try {
      const res = await getGuest(guest.id);
      setGuestDetails(res.data);
      setShowPenaltyForm(false);
      setShowWaiverForm(false);
      setEditingAdjId(null);
      setPaymentDialogOpen(true);
      loadAdjustments(guest.id);
    } catch (err) {
      toast.error('Failed to open receipt form');
    }
  };

  const handleStartAddPenalty = async () => {
    setShowWaiverForm(false);
    setShowPenaltyForm(true);
    try {
      const res = await getPenaltySuggestion(guestDetails.id);
      setPenaltyRate(res.data.per_day_rate || 100);
      setPenaltyForm({
        amount: res.data.suggested_amount || 0,
        reason: `Late payment: ${res.data.days_charged || 0} day(s) × ₹${res.data.per_day_rate || 100}`,
        month_year: '',
        days_charged: res.data.days_charged || 0,
        per_day_rate: res.data.per_day_rate || 100,
      });
    } catch (err) {
      setPenaltyForm({ amount: 0, reason: '', month_year: '', days_charged: 0, per_day_rate: 100 });
    }
  };

  const handleSavePenalty = async () => {
    if (!penaltyForm.amount || penaltyForm.amount <= 0) { toast.error('Enter a penalty amount'); return; }
    try {
      await createAdjustment({
        guest_id: guestDetails.id,
        adj_type: 'penalty',
        component: 'penalty',
        amount: parseFloat(penaltyForm.amount),
        reason: penaltyForm.reason || null,
        days_late: penaltyForm.days_charged || null,
        per_day_rate: penaltyForm.per_day_rate || null,
      });
      toast.success('Penalty added');
      setShowPenaltyForm(false);
      await loadAdjustments(guestDetails.id);
      const guestRes = await getGuest(guestDetails.id);
      setGuestDetails(guestRes.data);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to add penalty'));
    }
  };

  const handleSaveWaiver = async () => {
    if (!waiverForm.amount || waiverForm.amount <= 0) { toast.error('Enter a waiver amount'); return; }
    try {
      await createAdjustment({
        guest_id: guestDetails.id,
        adj_type: 'waiver',
        component: waiverForm.component,
        amount: parseFloat(waiverForm.amount),
        reason: waiverForm.reason || null,
      });
      toast.success('Waiver applied');
      setShowWaiverForm(false);
      setWaiverForm({ component: 'rent', amount: 0, reason: '' });
      await loadAdjustments(guestDetails.id);
      const guestRes = await getGuest(guestDetails.id);
      setGuestDetails(guestRes.data);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to apply waiver'));
    }
  };

  const handleDeleteAdjustment = async (adjId) => {
    try {
      await deleteAdjustment(adjId);
      toast.success('Removed');
      await loadAdjustments(guestDetails.id);
      const guestRes = await getGuest(guestDetails.id);
      setGuestDetails(guestRes.data);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to remove'));
    }
  };

  const handleSaveEditAdjustment = async (adjId) => {
    if (!editAdjForm.amount || editAdjForm.amount <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      await updateAdjustment(adjId, { amount: parseFloat(editAdjForm.amount), reason: editAdjForm.reason || null });
      toast.success('Updated');
      setEditingAdjId(null);
      await loadAdjustments(guestDetails.id);
      const guestRes = await getGuest(guestDetails.id);
      setGuestDetails(guestRes.data);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update'));
    }
  };

  const handleAdvanceRent = async () => {
    try {
      const res = await recordAdvanceRent({
        guest_id: guestDetails.id,
        months: advanceRentForm.months,
        payment_mode: advanceRentForm.payment_mode,
        payment_date: advanceRentForm.payment_date
      });
      toast.success(res.data.message);
      setAdvanceRentDialogOpen(false);
      setAdvanceRentForm({ months: 3, payment_mode: 'cash', payment_date: new Date().toISOString().split('T')[0] });
      // Refresh guest details
      const guestRes = await getGuest(guestDetails.id);
      setGuestDetails(guestRes.data);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to record advance rent'));
    }
  };

  const handleGiveNotice = async () => {
    try {
      const payload = { guest_id: selectedGuest.id };
      if (noticeDate) payload.notice_date = noticeDate;
      if (useAdvanceNotice && expectedLeaveDate) {
        payload.expected_leave_date = expectedLeaveDate;
      }
      const res = await giveNotice(payload);
      toast.success(`Notice given. Expected leave: ${formatDate(res.data.expected_leave_date)}`);
      setNoticeDialogOpen(false);
      setSelectedGuest(null);
      setNoticeDate('');
      setExpectedLeaveDate('');
      setUseAdvanceNotice(false);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to give notice'));
    }
  };

  const handleWithdrawNotice = async (guest) => {
    if (!window.confirm('Withdraw notice for this guest?')) return;
    try {
      await withdrawNotice({ guest_id: guest.id });
      toast.success('Notice withdrawn');
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to withdraw notice'));
    }
  };

  const handleReverseLeave = async (guest) => {
    if (!window.confirm(`Reverse leave for ${guest.name}? This will restore them as an active guest.`)) return;
    try {
      let restoreStatus = 'joined';
      if (guest.booking_type === 'day_basis') restoreStatus = 'day_basis';
      else if (guest.notice_date) restoreStatus = 'notice';
      const res = await reverseLeave({ guest_id: guest.id, restore_to_status: restoreStatus });
      toast.success(`Leave reversed. Guest restored to '${res?.data?.new_status || restoreStatus}' status.`);
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to reverse leave'));
    }
  };

  const handleProcessLeave = async () => {
    try {
      const payload = { guest_id: selectedGuest.id, ...leaveForm };
      if (selectedGuest?.status === 'notice') payload.immediate = false;
      await processLeave(payload);
      toast.success('Leave processed');
      setLeaveDialogOpen(false);
      setSelectedGuest(null);
      setLeaveForm({ leave_date: '', immediate: false });
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to process leave'));
    }
  };

  const handleConvertToJoined = async () => {
    try {
      // Use the dedicated join-booked endpoint which sets rent_due_day = joining day
      await joinBookedGuest({
        guest_id: selectedGuest.id,
        joining_date: convertJoinedForm.joining_date,
        align_to_first: false,  // Default: due date = joining day (NOT 1st)
        additional_deposit: 0,
        rent_paid: 0
      });
      toast.success('Guest converted to Joined successfully');
      setConvertJoinedDialogOpen(false);
      setSelectedGuest(null);
      setConvertJoinedForm({ joining_date: '' });
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to convert guest'));
    }
  };

  const handleCancelBooking = async () => {
    try {
      await updateGuest(selectedGuest.id, {
        status: 'left',
        actual_leave_date: cancelBookingForm.cancellation_date,
        cancellation_reason: cancelBookingForm.reason
      });
      toast.success('Booking cancelled successfully');
      setCancelBookingDialogOpen(false);
      setSelectedGuest(null);
      setCancelBookingForm({ cancellation_date: '', reason: '' });
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to cancel booking'));
    }
  };

  const handleShift = async () => {
    try {
      await shiftGuest({ guest_id: selectedGuest.id, ...shiftForm });
      toast.success('Guest shifted successfully');
      setShiftDialogOpen(false);
      setSelectedGuest(null);
      setShiftForm({ new_room_id: '', new_bed_no: 1, shift_date: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to shift guest'));
    }
  };

  const handleSettleDues = async () => {
    try {
      // Calculate total outstanding
      const totalDue = (guestDetails.rent_due || 0) + (guestDetails.deposit_due || 0) + (guestDetails.electricity_due || 0);
      const totalCredit = (guestDetails.deposit_paid || 0) + (guestDetails.advance_rent_balance || 0);
      const netBalance = totalCredit - totalDue;
      
      // Update guest with zeroed dues
      const updateData = {
        rent_due: 0,
        deposit_due: 0,
        electricity_due: 0,
        deposit_paid: 0,
        advance_rent_balance: 0,
        settlement_amount: netBalance,
        settlement_date: getISTDateString(),
        settlement_remarks: settleDuesForm.remarks || `Settled: ${netBalance >= 0 ? 'Credit' : 'Due'} of ₹${Math.abs(netBalance).toLocaleString()} written off`
      };
      
      await updateGuest(guestDetails.id, updateData);
      toast.success(`Dues settled successfully. ${netBalance >= 0 ? 'Credit' : 'Due'} of ₹${Math.abs(netBalance).toLocaleString()} written off.`);
      setSettleDuesDialogOpen(false);
      setSettleDuesForm({ settlement_type: 'write_off', remarks: '' });
      handleViewGuest(guestDetails); // Refresh guest details
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to settle dues'));
    }
  };

  const handleDeleteGuest = async (guest) => {
    if (!window.confirm(`Are you sure you want to delete "${guest.name}"? This will also delete all related payments and hisab entries.`)) return;
    try {
      await deleteGuest(guest.id);
      toast.success('Guest deleted successfully');
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete guest'));
    }
  };

  const filteredRooms = form.property_id 
    ? rooms.filter(r => r.property_id === form.property_id)
    : rooms;

  const shiftFilteredRooms = rooms.filter(r => r.beds?.some(b => b.status === 'vacant'));

  const guestHasDues = (g) => ((g.advance_rent_due || 0) > 0 && g.status !== 'booked') || ((g.electricity_due || 0) > 0) || ((g.deposit_due || 0) > 0);

  // Rent due day-of-month for monthly guests (parsed from rent_due_day or the cycle label)
  const getDueDay = (g) => {
    if (g.rent_due_day) return parseInt(g.rent_due_day);
    const m = (g.rent_cycle_label || '').match(/\d+/);
    return m ? parseInt(m[0]) : null;
  };
  const ordinal = (n) => { const s = ['th', 'st', 'nd', 'rd'], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };
  // Distinct due days actually present (monthly guests only) for the finer dropdown
  const dueDayOptions = [...new Set(
    guests.filter(g => g.status !== 'booked' && g.status !== 'day_basis').map(getDueDay).filter(Boolean)
  )].sort((a, b) => a - b);

  const filtersActive = searchQuery || selectedProperty !== 'all' || selectedStatus !== 'all' || onlyDues || joinRange?.from || dueDayFilter !== 'all';
  const clearAllFilters = () => {
    setSearchQuery(''); setSelectedProperty('all'); setSelectedStatus('all');
    setOnlyDues(false); setJoinRange(undefined); setDueDayFilter('all');
  };

  const visibleGuests = [...guests]
    .filter(guest => {
      if (selectedStatus === 'overstay') return guest.display_type === 'overstay';
      return true;
    })
    .filter(guest => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return (guest.name || '').toLowerCase().includes(q)
        || (guest.mobile || '').toLowerCase().includes(q)
        || (guest.guest_code || '').toLowerCase().includes(q)
        || (guest.room_info || '').toLowerCase().includes(q);
    })
    .filter(guest => {
      if (!joinRange?.from) return true;
      const jd = guest.joining_date || guest.booking_date;
      if (!jd) return false;
      const d = new Date(jd); d.setHours(0, 0, 0, 0);
      const from = new Date(joinRange.from); from.setHours(0, 0, 0, 0);
      const to = joinRange.to ? new Date(joinRange.to) : new Date(joinRange.from); to.setHours(23, 59, 59, 999);
      return d >= from && d <= to;
    })
    .filter(guest => {
      if (dueDayFilter === 'all') return true;
      return getDueDay(guest) === parseInt(dueDayFilter);
    })
    .filter(guest => !onlyDues || guestHasDues(guest))
    .sort((a, b) => (a.room_info || '').localeCompare(b.room_info || '', undefined, { numeric: true }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-800" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="guests-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Guests</h1>
          <p className="text-slate-500 mt-1">Manage guest bookings and accommodations</p>
        </div>
        <div className="flex gap-2">
          <Button 
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={async () => {
              try {
                const params = {};
                if (selectedProperty) params.property_id = selectedProperty;
                if (selectedStatus && selectedStatus !== 'all') params.status = selectedStatus;
                const response = await exportGuestList(params);
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `guest_list_${new Date().toISOString().split('T')[0]}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success('Guest list downloaded successfully');
              } catch (err) {
                toast.error('Failed to download guest list');
              }
            }}
            data-testid="download-guest-list-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Excel
          </Button>
          {canAdd('guests') && (
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-800 hover:bg-blue-900" data-testid="add-guest-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Guest
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Add New Guest</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddGuest} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value.toUpperCase() })} required data-testid="guest-name-input" style={{ textTransform: 'uppercase' }} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Mobile</Label>
                    <Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} required data-testid="guest-mobile-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Emergency Mobile</Label>
                    <Input value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} required maxLength={10} data-testid="guest-emergency-input" />
                  </div>
                </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Property</Label>
                  <Select value={form.property_id} onValueChange={(val) => setForm({ ...form, property_id: val, room_id: '' })}>
                    <SelectTrigger data-testid="guest-property-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Room</Label>
                  <Select value={form.room_id} onValueChange={(val) => setForm({ ...form, room_id: val })} disabled={!form.property_id}>
                    <SelectTrigger data-testid="guest-room-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      {filteredRooms.map(r => <SelectItem key={r.id} value={r.id}>{r.combined_number}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bed No</Label>
                  <Select value={form.bed_no.toString()} onValueChange={(val) => setForm({ ...form, bed_no: parseInt(val) })} disabled={!form.room_id}>
                    <SelectTrigger data-testid="guest-bed-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {availableBeds.map(b => <SelectItem key={b} value={b.toString()}>Bed {b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(val) => setForm({ ...form, status: val, booking_type: val === 'day_basis' ? 'day_basis' : 'monthly' })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="joined">Joined</SelectItem>
                      <SelectItem value="day_basis">Day Basis</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Rent & Deposit (auto-filled from the selected bed; editing also updates the bed) */}
              {form.status === 'joined' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Monthly Rent (₹)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.monthly_rent}
                      onChange={(e) => setForm({ ...form, monthly_rent: e.target.value })}
                      disabled={!form.room_id}
                      data-testid="guest-rent-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Deposit (₹)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={form.deposit}
                      onChange={(e) => setForm({ ...form, deposit: e.target.value })}
                      disabled={!form.room_id}
                      data-testid="guest-deposit-input"
                    />
                  </div>
                </div>
              )}

              {/* Day Basis Options */}
              {form.status === 'day_basis' && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200 space-y-4">
                  <h4 className="font-medium text-purple-700">Day Basis Booking</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-purple-700">Number of Days</Label>
                      <Input
                        type="number"
                        min="1"
                        value={form.num_days}
                        onChange={(e) => setForm({ ...form, num_days: parseInt(e.target.value) || 1 })}
                        data-testid="guest-num-days"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-purple-700">Daily Rate (₹)</Label>
                      <Input 
                        type="number" 
                        min="0" 
                        value={form.daily_rate} 
                        onChange={(e) => setForm({ ...form, daily_rate: parseFloat(e.target.value) || 0 })} 
                        data-testid="guest-daily-rate" 
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-purple-700">Check-in Date</Label>
                      <Input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} data-testid="guest-checkin-date" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-purple-700">Check-out Date</Label>
                      <Input 
                        type="date" 
                        value={form.joining_date ? (() => {
                          const checkin = new Date(form.joining_date);
                          checkin.setDate(checkin.getDate() + form.num_days);
                          return checkin.toISOString().split('T')[0];
                        })() : ''} 
                        readOnly
                        className="bg-purple-100 cursor-not-allowed"
                        data-testid="guest-checkout-date" 
                      />
                    </div>
                  </div>
                  <div className="bg-purple-100 p-3 rounded text-center">
                    <p className="text-sm text-purple-600">Total Amount</p>
                    <p className="text-2xl font-bold font-mono text-purple-700">₹{(form.num_days * form.daily_rate).toLocaleString()}</p>
                    <p className="text-xs text-purple-500">{form.num_days} day{form.num_days > 1 ? 's' : ''} × ₹{form.daily_rate}/day</p>
                  </div>
                </div>
              )}

              {/* Show Joining Date when status is "joined" */}
              {form.status === 'joined' && (
                <div className="space-y-2">
                  <Label>Joining Date</Label>
                  <Input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} data-testid="guest-joining-date" />
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)} className="flex-1">Cancel</Button>
                <Button type="submit" className="flex-1 bg-blue-800 hover:bg-blue-900" data-testid="save-guest-btn">Add Guest</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl" data-testid="guest-filter-bar">
        <div className="relative w-full sm:w-[260px]">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search name, mobile or room..."
            className="pl-9 bg-white"
            data-testid="guest-search-input"
          />
        </div>
        <Select value={selectedProperty} onValueChange={setSelectedProperty}>
          <SelectTrigger className="w-full sm:w-[180px] bg-white" data-testid="guest-property-filter">
            <SelectValue placeholder="All Properties" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Properties</SelectItem>
            {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-full sm:w-[150px] bg-white" data-testid="guest-status-filter">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="joined">Joined</SelectItem>
            <SelectItem value="occupied">Occupied</SelectItem>
            <SelectItem value="day_basis">Day Basis</SelectItem>
            <SelectItem value="notice">Notice</SelectItem>
            <SelectItem value="overstay">Overstay</SelectItem>
            <SelectItem value="left">Left</SelectItem>
          </SelectContent>
        </Select>

        {/* Joining date range */}
        <Popover open={joinPopoverOpen} onOpenChange={setJoinPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={`w-full sm:w-auto justify-start text-left font-normal bg-white ${joinRange?.from ? 'border-blue-400 text-blue-700' : 'text-slate-600'}`}
              data-testid="guest-joining-filter"
            >
              <Calendar className="h-4 w-4 mr-2 shrink-0" />
              {joinRange?.from
                ? (joinRange.to
                    ? `${formatFns(joinRange.from, 'dd MMM yy')} – ${formatFns(joinRange.to, 'dd MMM yy')}`
                    : formatFns(joinRange.from, 'dd MMM yy'))
                : 'Joining date'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarPicker
              mode="range"
              selected={joinRange}
              onSelect={setJoinRange}
              numberOfMonths={2}
              initialFocus
            />
            <div className="flex items-center justify-between border-t p-2">
              <Button variant="ghost" size="sm" onClick={() => setJoinRange(undefined)} data-testid="guest-joining-clear">
                Clear
              </Button>
              <Button size="sm" className="bg-blue-800 hover:bg-blue-900" onClick={() => setJoinPopoverOpen(false)} data-testid="guest-joining-done">
                Done
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Due date (rent due day-of-month) */}
        <Select value={dueDayFilter} onValueChange={setDueDayFilter}>
          <SelectTrigger className="w-full sm:w-[160px] bg-white" data-testid="guest-duedate-filter">
            <SelectValue placeholder="All Due Dates" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Due Dates</SelectItem>
            {dueDayOptions.map(d => <SelectItem key={d} value={String(d)}>{ordinal(d)} of month</SelectItem>)}
          </SelectContent>
        </Select>

        <button
          onClick={() => setOnlyDues(v => !v)}
          className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium shadow-sm transition-colors ${onlyDues ? 'bg-rose-600 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}
          data-testid="has-dues-toggle"
        >
          <span className={`w-2 h-2 rounded-full ${onlyDues ? 'bg-white' : 'bg-rose-500'}`}></span>
          Has dues
        </button>

        {filtersActive && (
          <button
            onClick={clearAllFilters}
            className="text-sm text-slate-500 hover:text-slate-800 underline underline-offset-2 self-center ml-auto"
            data-testid="guest-clear-filters"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Guests Table */}
      <Card className="dash-animate" data-testid="guests-table">
        <CardContent className="p-0 overflow-x-auto">
          <Table className="whitespace-nowrap">
            <TableHeader>
              <TableRow>
                <TableHead>Guest ID</TableHead>
                <TableHead>Room</TableHead>
                <TableHead>Sharing</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Joining</TableHead>
                <TableHead>Deposit (₹)</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Rent (₹)</TableHead>
                <TableHead>Electricity (₹)</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {guests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8 text-slate-500">
                    No guests found
                  </TableCell>
                </TableRow>
              ) : visibleGuests.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-10 text-slate-500">
                    <Search className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                    <p className="font-medium text-slate-700">No guests match your filters</p>
                    <p className="text-sm">Try a different search or turn off "Has dues".</p>
                  </TableCell>
                </TableRow>
              ) : visibleGuests.map((guest) => {
                // Row highlighting for special states
                const rowClass = guest.id === highlightGuestId
                  ? 'bg-blue-100 border-l-4 border-l-blue-500'
                  : guest.display_type === 'overstay' 
                  ? 'bg-red-50 border-l-4 border-l-red-500 hover:bg-red-100/70' 
                  : guest.is_leaving_today 
                  ? 'bg-orange-50 border-l-4 border-l-orange-500 hover:bg-orange-100/70'
                  : 'hover:bg-slate-50';
                return (
                <TableRow key={guest.id} data-testid={`guest-row-${guest.id}`} className={`transition-colors ${rowClass}`}>
                  <TableCell>
                    <span className="px-2 py-0.5 rounded text-xs font-mono font-semibold bg-indigo-50 text-indigo-700 whitespace-nowrap inline-block" data-testid={`guest-code-${guest.id}`}>
                      {guest.guest_code || '—'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm font-mono">{guest.room_info}</p>
                  </TableCell>
                  <TableCell>
                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                      {guest.sharing_type || '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <StatusBadge 
                      status={guest.status} 
                      displayType={guest.display_type} 
                      isLeavingToday={guest.is_leaving_today}
                      overstayDays={guest.overstay_days}
                    />
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{guest.name}</p>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm text-slate-600 font-mono">{guest.mobile}</p>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{formatDate(guest.joining_date || guest.booking_date)}</TableCell>
                  <TableCell>
                    <div>
                      <p className="font-mono text-sm">₹{((guest.deposit_paid || 0) + (guest.deposit_due || 0)).toLocaleString()}</p>
                      {guest.deposit_due > 0 && (
                        <p className="text-xs text-rose-600">Due: ₹{guest.deposit_due.toLocaleString()}</p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {guest.status === 'booked' ? (
                      <span className="text-xs text-slate-400">-</span>
                    ) : guest.status === 'day_basis' ? (
                      <div>
                        {(() => {
                          // Use backend-provided effective (paid) checkout — single source of truth
                          const numDays = guest.effective_days || guest.num_days || 1;
                          const checkoutStr = guest.effective_checkout;
                          if (checkoutStr) {
                            const checkoutDate = new Date(checkoutStr);
                            const isOverdue = guest.display_type === 'overstay' || (guest.overstay_days || 0) > 0;
                            const isToday = !!guest.is_leaving_today;
                            return (
                              <>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${isOverdue ? 'bg-rose-100 text-rose-700' : isToday ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'}`}>
                                  {isOverdue ? 'Overdue' : isToday ? 'Today' : `${numDays} days`}
                                </span>
                                <p className="text-xs text-slate-500 mt-0.5">
                                  Out: {formatDateObj(checkoutDate)}
                                </p>
                              </>
                            );
                          }
                          return <span className="text-xs text-purple-600">{numDays} days</span>;
                        })()}
                      </div>
                    ) : (
                      <div>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${guest.rent_cycle_type === 'standard' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                          {guest.rent_cycle_label || '1st'}
                        </span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {guest.status === 'day_basis' ? (
                      <div>
                        <p className="font-mono">₹{(guest.daily_rate || 0).toLocaleString()}</p>
                        {guest.advance_rent_due > 0 && (
                          <p className="text-xs text-rose-600">Due: ₹{guest.advance_rent_due.toLocaleString()}</p>
                        )}
                      </div>
                    ) : (
                    <div>
                      <p className="font-mono">₹{(guest.monthly_rent || 0).toLocaleString()}</p>
                      {/* Show Pro-rated ONLY if: prorated_rent is less than monthly_rent (actual partial month rent) 
                          AND not for overstay/notice guests (they have separate overstay rent calculation) */}
                      {guest.prorated_rent > 0 && 
                       guest.prorated_rent < guest.monthly_rent && 
                       !['notice', 'overstay'].includes(guest.display_type) &&
                       guest.status !== 'booked' && (
                        <p className="text-xs text-amber-600">Pro-rated: ₹{guest.prorated_rent.toLocaleString()}</p>
                      )}
                      {guest.advance_rent_due > 0 && guest.status !== 'booked' && (
                        <p className="text-xs text-rose-600">Due: ₹{guest.advance_rent_due.toLocaleString()}</p>
                      )}
                    </div>
                    )}
                  </TableCell>
                  {/* Electricity Column - Same format as Rent */}
                  <TableCell>
                    {guest.status === 'day_basis' || guest.status === 'booked' ? (
                      <span className="text-xs text-slate-400">-</span>
                    ) : (
                      <div>
                        <p className="font-mono">₹{(guest.electricity_due || 0).toLocaleString()}</p>
                        {(guest.electricity_due || 0) > 0 && (
                          <p className="text-xs text-yellow-600">Due: ₹{(guest.electricity_due || 0).toLocaleString()}</p>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {canEdit('guests') && guest.status !== 'booked' && (
                        <IconTip label="Collect Receipt"><Button variant="ghost" size="icon" onClick={() => openCollectForGuest(guest)} className="h-8 w-8 text-emerald-600" data-testid={`collect-receipt-${guest.id}`}>
                          <Banknote className="h-4 w-4" />
                        </Button></IconTip>
                      )}
                      <IconTip label="View Details"><Button variant="ghost" size="icon" onClick={() => handleViewGuest(guest)} className="h-8 w-8" data-testid={`view-guest-${guest.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button></IconTip>
                      {canEdit('guests') && guest.status === 'booked' && (
                        <>
                          <IconTip label="Convert to Joined"><Button variant="ghost" size="icon" onClick={() => { setSelectedGuest(guest); setConvertJoinedForm({ joining_date: getISTDateString() }); setConvertJoinedDialogOpen(true); }} className="h-8 w-8 text-emerald-600" data-testid={`convert-joined-${guest.id}`}>
                            <Users className="h-4 w-4" />
                          </Button></IconTip>
                          <IconTip label="Cancel Booking"><Button variant="ghost" size="icon" onClick={() => { setSelectedGuest(guest); setCancelBookingForm({ cancellation_date: getISTDateString(), reason: '' }); setCancelBookingDialogOpen(true); }} className="h-8 w-8 text-rose-600" data-testid={`cancel-booking-${guest.id}`}>
                            <X className="h-4 w-4" />
                          </Button></IconTip>
                        </>
                      )}
                      {canEdit('guests') && guest.status === 'joined' && (
                        <>
                          <IconTip label="Notice"><Button variant="ghost" size="icon" onClick={() => { setSelectedGuest(guest); setNoticeDialogOpen(true); }} className="h-8 w-8 text-amber-600" data-testid={`notice-guest-${guest.id}`}>
                            <Clock className="h-4 w-4" />
                          </Button></IconTip>
                          <IconTip label="Shift"><Button variant="ghost" size="icon" onClick={async () => { setSelectedGuest(guest); try { const res = await getRooms(); const sorted = [...res.data].sort((a, b) => (a.combined_number || '').localeCompare(b.combined_number || '', undefined, { numeric: true, sensitivity: 'base' })); setRooms(sorted); } catch(e) {} setShiftDialogOpen(true); }} className="h-8 w-8 text-purple-600" data-testid={`shift-guest-${guest.id}`}>
                            <ArrowRightLeft className="h-4 w-4" />
                          </Button></IconTip>
                          <IconTip label="Immediate Leave"><Button variant="ghost" size="icon" onClick={() => { setSelectedGuest(guest); setLeaveDialogOpen(true); }} className="h-8 w-8 text-rose-600" data-testid={`immediate-leave-${guest.id}`}>
                            <LogOut className="h-4 w-4" />
                          </Button></IconTip>
                        </>
                      )}
                      {canEdit('guests') && guest.status === 'notice' && (
                        <>
                          <IconTip label="Withdraw Notice"><Button variant="ghost" size="icon" onClick={() => handleWithdrawNotice(guest)} className="h-8 w-8 text-emerald-600" data-testid={`withdraw-notice-${guest.id}`}>
                            <RotateCcw className="h-4 w-4" />
                          </Button></IconTip>
                          <IconTip label="Process Leave"><Button variant="ghost" size="icon" onClick={() => { setSelectedGuest(guest); setLeaveDialogOpen(true); }} className="h-8 w-8 text-rose-600" data-testid={`leave-guest-${guest.id}`}>
                            <LogOut className="h-4 w-4" />
                          </Button></IconTip>
                        </>
                      )}
                      {canEdit('guests') && guest.status === 'day_basis' && (
                        <IconTip label="Process Leave"><Button variant="ghost" size="icon" onClick={() => { setSelectedGuest(guest); setLeaveDialogOpen(true); }} className="h-8 w-8 text-rose-600" data-testid={`leave-day-basis-${guest.id}`}>
                          <LogOut className="h-4 w-4" />
                        </Button></IconTip>
                      )}
                      {canEdit('guests') && guest.status === 'left' && (
                        <IconTip label="Reverse Leave"><Button variant="ghost" size="icon" onClick={() => handleReverseLeave(guest)} className="h-8 w-8 text-emerald-600" data-testid={`reverse-leave-${guest.id}`}>
                          <RotateCcw className="h-4 w-4" />
                        </Button></IconTip>
                      )}
                      {canDelete('guests') && (
                        <IconTip label="Delete Guest"><Button variant="ghost" size="icon" onClick={() => handleDeleteGuest(guest)} className="h-8 w-8 text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`delete-guest-${guest.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button></IconTip>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )})}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* View/Edit Guest Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={(open) => { setViewDialogOpen(open); if (!open) setIsEditing(false); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Guest Details</span>
              {guestDetails && guestDetails.status !== 'left' && canEdit('guests') && (
                <div className="flex gap-2">
                  {!isEditing ? (
                    <Button variant="outline" size="sm" onClick={() => setIsEditing(true)} data-testid="edit-guest-btn">
                      <Pencil className="h-4 w-4 mr-1" /> Edit
                    </Button>
                  ) : (
                    <>
                      <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                        <X className="h-4 w-4 mr-1" /> Cancel
                      </Button>
                      <Button size="sm" onClick={handleSaveEdit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-guest-edit-btn">
                        <Save className="h-4 w-4 mr-1" /> Save
                      </Button>
                    </>
                  )}
                </div>
              )}
            </DialogTitle>
          </DialogHeader>
          {guestDetails && (
            <div className="space-y-4 mt-4">
              {/* Guest profile card + KYC documents */}
              <GuestKYC guestId={guestDetails.id} guest={guestDetails} canEdit={guestDetails.status !== 'left' && canEdit('guests')} />
              {/* Basic Info — only shown while editing (view mode is covered by the profile card) */}
              {isEditing && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Name</Label>
                  <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value.toUpperCase() })} data-testid="edit-guest-name" />
                </div>
                <div>
                  <Label className="text-slate-500">Mobile</Label>
                  <Input value={editForm.mobile} onChange={(e) => setEditForm({ ...editForm, mobile: e.target.value })} data-testid="edit-guest-mobile" />
                </div>
                <div>
                  <Label className="text-slate-500">Emergency Mobile</Label>
                  <Input value={editForm.emergency_contact} onChange={(e) => setEditForm({ ...editForm, emergency_contact: e.target.value })} maxLength={10} data-testid="edit-guest-emergency" />
                </div>
                <div>
                  <Label className="text-slate-500">Joining Date</Label>
                  <Input type="date" value={editForm.joining_date} onChange={(e) => setEditForm({ ...editForm, joining_date: e.target.value })} />
                </div>
              </div>
              )}

              {/* Financial Details */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold">Financial Details</h4>
                  {/* Show payment buttons for active guests OR left guests with outstanding dues OR booked guests for deposit */}
                  {canEdit('receipts') && (guestDetails.status === 'booked' || (
                    guestDetails.status !== 'left' || 
                    (guestDetails.advance_rent_due > 0 || guestDetails.deposit_due > 0 || guestDetails.electricity_due > 0)
                  )) && (
                    <div className="flex gap-2 flex-wrap">
                      {/* Advance Rent only for active guests (not booked or left) */}
                      {guestDetails.status !== 'left' && guestDetails.status !== 'booked' && (
                        <Button size="sm" variant="outline" onClick={() => setAdvanceRentDialogOpen(true)} className="text-purple-600" data-testid="advance-rent-btn">
                          <Calendar className="h-4 w-4 mr-1" /> Advance Rent
                        </Button>
                      )}
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="bg-emerald-50 p-3 rounded-lg">
                    <p className="text-slate-500">Deposit Paid</p>
                    {isEditing ? (
                      <Input type="number" min="0" className="mt-1" value={editForm.deposit_paid} onChange={(e) => setEditForm({ ...editForm, deposit_paid: parseFloat(e.target.value) || 0 })} />
                    ) : (
                      <p className="text-lg font-mono text-emerald-700">₹{guestDetails.deposit_paid}</p>
                    )}
                  </div>
                  <div className="bg-amber-50 p-3 rounded-lg">
                    <p className="text-slate-500">Deposit Due</p>
                    {isEditing ? (
                      <Input type="number" min="0" className="mt-1" value={editForm.deposit_due} onChange={(e) => setEditForm({ ...editForm, deposit_due: parseFloat(e.target.value) || 0 })} />
                    ) : (
                      <p className="text-lg font-mono text-amber-700">₹{guestDetails.deposit_due}</p>
                    )}
                  </div>
                  <div className="bg-emerald-50 p-3 rounded-lg">
                    <p className="text-slate-500">Rent Paid</p>
                    {isEditing ? (
                      <Input type="number" min="0" className="mt-1" value={editForm.advance_rent_paid} onChange={(e) => setEditForm({ ...editForm, advance_rent_paid: parseFloat(e.target.value) || 0 })} />
                    ) : (
                      <p className="text-lg font-mono text-emerald-700">₹{guestDetails.advance_rent_paid}</p>
                    )}
                  </div>
                  <div className="bg-amber-50 p-3 rounded-lg">
                    <p className="text-slate-500">Rent Due</p>
                    {isEditing ? (
                      <Input type="number" min="0" className="mt-1" value={editForm.advance_rent_due} onChange={(e) => setEditForm({ ...editForm, advance_rent_due: parseFloat(e.target.value) || 0 })} />
                    ) : (
                      <p className="text-lg font-mono text-amber-700">₹{guestDetails.advance_rent_due}</p>
                    )}
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg col-span-2">
                    <p className="text-slate-500">{guestDetails.status === 'day_basis' ? 'Daily Rate' : 'Monthly Rent'}</p>
                    {guestDetails.status === 'day_basis' ? (
                      <p className="text-lg font-mono text-blue-700">₹{(guestDetails.daily_rate || 0).toLocaleString()}/day</p>
                    ) : isEditing ? (
                      <>
                        <Input type="number" min="0" className="mt-1" value={editForm.monthly_rent} onChange={(e) => setEditForm({ ...editForm, monthly_rent: parseFloat(e.target.value) || 0 })} />
                        {/* Show cycle selection when rent is being changed */}
                        {editForm.monthly_rent !== guestDetails.monthly_rent && editForm.monthly_rent > 0 && (() => {
                          const now = new Date();
                          const rentDueDay = guestDetails.rent_due_day || 1;
                          let nextCycleDate, currentCycleDate;
                          
                          if (guestDetails.rent_cycle_type === 'standard') {
                            currentCycleDate = new Date(now.getFullYear(), now.getMonth(), 1);
                            if (now.getDate() === 1) {
                              nextCycleDate = new Date(now.getFullYear(), now.getMonth(), 1);
                            } else {
                              nextCycleDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                            }
                          } else {
                            if (now.getDate() >= rentDueDay) {
                              currentCycleDate = new Date(now.getFullYear(), now.getMonth(), rentDueDay);
                              nextCycleDate = new Date(now.getFullYear(), now.getMonth() + 1, rentDueDay);
                            } else {
                              currentCycleDate = new Date(now.getFullYear(), now.getMonth() - 1, rentDueDay);
                              nextCycleDate = new Date(now.getFullYear(), now.getMonth(), rentDueDay);
                            }
                          }
                          
                          const fmt = (d) => formatDateObj(d);
                          const effectiveDate = editForm.rent_from_current_cycle ? currentCycleDate : nextCycleDate;
                          
                          return (
                            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm space-y-2">
                              <p className="font-medium text-blue-900 text-xs">When should new rent apply?</p>
                              <label className="flex items-center gap-2 cursor-pointer" data-testid="rent-next-cycle-radio">
                                <input type="radio" name="rent_cycle_choice" checked={!editForm.rent_from_current_cycle} onChange={() => setEditForm({ ...editForm, rent_from_current_cycle: false })} className="accent-blue-600" />
                                <span className="text-slate-700">From next billing cycle <span className="text-slate-500 text-xs">({fmt(nextCycleDate)})</span></span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer" data-testid="rent-current-cycle-radio">
                                <input type="radio" name="rent_cycle_choice" checked={editForm.rent_from_current_cycle === true} onChange={() => setEditForm({ ...editForm, rent_from_current_cycle: true })} className="accent-blue-600" />
                                <span className="text-slate-700">From current billing cycle <span className="text-slate-500 text-xs">({fmt(currentCycleDate)})</span></span>
                              </label>
                              <div className={`mt-1 p-2 rounded text-xs ${editForm.rent_from_current_cycle ? 'bg-amber-100 border border-amber-300 text-amber-800' : 'bg-blue-100 border border-blue-300 text-blue-800'}`}>
                                <p className="font-medium">New rent of ₹{editForm.monthly_rent?.toLocaleString()} effective from: {fmt(effectiveDate)}</p>
                                {editForm.rent_from_current_cycle && <p className="mt-0.5">This will recalculate dues for the current cycle at the new rent.</p>}
                              </div>
                            </div>
                          );
                        })()}
                      </>
                    ) : (
                      <p className="text-lg font-mono text-blue-700">₹{guestDetails.monthly_rent}/month</p>
                    )}
                  </div>
                  {/* Electricity Section - Not shown for Day Basis guests */}
                  {guestDetails.status !== 'day_basis' && guestDetails.booking_type !== 'day_basis' && (
                    <>
                      <div className="bg-emerald-50 p-3 rounded-lg">
                        <p className="text-slate-500">Electricity Paid</p>
                        {isEditing ? (
                          <Input type="number" min="0" className="mt-1" value={editForm.electricity_paid} onChange={(e) => setEditForm({ ...editForm, electricity_paid: parseFloat(e.target.value) || 0 })} />
                        ) : (
                          <p className="text-lg font-mono text-emerald-700">₹{guestDetails.electricity_paid || 0}</p>
                        )}
                      </div>
                      <div className="bg-yellow-50 p-3 rounded-lg">
                        <p className="text-slate-500">Electricity Due</p>
                        {isEditing ? (
                          <Input type="number" min="0" className="mt-1" value={editForm.electricity_due} onChange={(e) => setEditForm({ ...editForm, electricity_due: parseFloat(e.target.value) || 0 })} />
                        ) : (
                          <p className="text-lg font-mono text-yellow-700">₹{guestDetails.electricity_due || 0}</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
                
                {/* Due Date Section - Not shown for Day Basis or Left or Booked guests */}
                {guestDetails.status !== 'left' && guestDetails.status !== 'booked' && guestDetails.status !== 'day_basis' && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-sm font-medium text-amber-800">Rent Due Date</p>
                        <p className="text-xs text-amber-600">
                          Currently: <span className="font-mono font-bold">{guestDetails.rent_due_day === 1 ? '1st' : `${guestDetails.rent_due_day}${guestDetails.rent_due_day === 2 ? 'nd' : guestDetails.rent_due_day === 3 ? 'rd' : 'th'}`}</span> of each month
                          {guestDetails.rent_cycle_type === 'standard' && <span className="ml-1 text-emerald-600">(Pro-rated)</span>}
                        </p>
                      </div>
                      {canEdit('guests') && guestDetails.rent_due_day !== 1 && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="text-amber-700 border-amber-400 hover:bg-amber-100"
                          onClick={async () => {
                            if (window.confirm('Change due date to 1st of each month?\n\nThis will calculate pro-rated rent for the joining month.')) {
                              try {
                                const res = await changeDueDate({ guest_id: guestDetails.id, new_due_day: 1 });
                                toast.success(res.data.message);
                                if (res.data.prorated_rent) {
                                  toast.info(`Pro-rated rent: ₹${res.data.prorated_rent}`);
                                }
                                // Refresh guest details
                                const guestRes = await getGuest(guestDetails.id);
                                setGuestDetails(guestRes.data);
                                fetchData();
                              } catch (err) {
                                toast.error(getErrorMessage(err, 'Failed to change due date'));
                              }
                            }
                          }}
                          data-testid="change-due-date-btn"
                        >
                          <Calendar className="h-4 w-4 mr-1" />
                          Change to 1st
                        </Button>
                      )}
                    </div>
                    {guestDetails.prorated_rent > 0 && (
                      <p className="text-xs text-amber-700 mt-1">
                        Pro-rated rent for joining month: <span className="font-mono font-bold">₹{guestDetails.prorated_rent}</span>
                      </p>
                    )}
                  </div>
                )}

                {/* Checkout Section - Only for Day Basis guests */}
                {guestDetails.status === 'day_basis' && guestDetails.joining_date && (
                  <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    {(() => {
                      // Use backend-provided effective (paid) checkout — single source of truth
                      const numDays = guestDetails.effective_days || guestDetails.num_days || 1;
                      const bookedDays = guestDetails.num_days || 1;
                      const checkoutStr = guestDetails.effective_checkout;
                      const checkoutDate = checkoutStr ? new Date(checkoutStr) : (() => {
                        const d = new Date(guestDetails.joining_date);
                        d.setDate(d.getDate() + numDays);
                        return d;
                      })();
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const isOverdue = guestDetails.display_type === 'overstay' || (guestDetails.overstay_days || 0) > 0;
                      const isToday = !!guestDetails.is_leaving_today;
                      const overstayDays = guestDetails.overstay_days || 0;
                      const daysRemaining = Math.max(0, Math.ceil((checkoutDate - today) / (1000 * 60 * 60 * 24)));
                      const isExtended = numDays > bookedDays;
                      
                      return (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-purple-800">Day Basis Booking</p>
                            <p className="text-xs text-purple-600">
                              Stay: <span className="font-mono font-bold">{numDays} day{numDays > 1 ? 's' : ''}</span>
                              {isExtended && <span className="ml-1 text-emerald-600">(extended from {bookedDays})</span>}
                              {' • '}Daily Rate: <span className="font-mono font-bold">₹{guestDetails.daily_rate || 0}</span>
                            </p>
                            <p className="text-xs text-purple-600 mt-1">
                              Check-out: <span className="font-mono font-bold">{formatDateObj(checkoutDate)}</span>
                            </p>
                          </div>
                          <div className="text-right">
                            {isOverdue ? (
                              <div className="px-3 py-1 bg-rose-100 text-rose-700 rounded-full text-sm font-medium">
                                ⚠️ Overdue ({overstayDays} day{overstayDays > 1 ? 's' : ''})
                              </div>
                            ) : isToday ? (
                              <div className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">
                                🕐 Checkout Today
                              </div>
                            ) : (
                              <div className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                                ✓ {daysRemaining} day{daysRemaining > 1 ? 's' : ''} left
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Notice Info */}
              {guestDetails.notice_date && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3 text-amber-700">Notice Period</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-slate-500">Notice Date</Label><p className="font-mono">{formatDate(guestDetails.notice_date)}</p></div>
                    <div><Label className="text-slate-500">Expected Leave</Label><p className="font-mono">{formatDate(guestDetails.expected_leave_date)}</p></div>
                  </div>
                </div>
              )}

              {/* Leave Info - for 'left' status guests (Reverse Leave button is in Action column) */}
              {guestDetails.status === 'left' && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3 text-slate-700">Guest Has Left</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label className="text-slate-500">Leave Date</Label><p className="font-mono">{guestDetails.actual_leave_date ? formatDate(guestDetails.actual_leave_date) : 'N/A'}</p></div>
                    <div><Label className="text-slate-500">Settlement</Label><p className="font-mono capitalize">{guestDetails.settlement_type || 'Normal'}</p></div>
                  </div>
                </div>
              )}

              {/* Receipt History */}
              {guestDetails.payments?.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Receipt History</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {guestDetails.payments.map((p) => (
                      <div key={p.id} className="flex justify-between items-center text-sm bg-slate-50 p-2 rounded">
                        <div>
                          <span className="font-medium capitalize">{p.payment_type}</span>
                          <span className="text-slate-500 ml-2">({p.payment_mode})</span>
                        </div>
                        <div className="text-right">
                          <span className="font-mono text-emerald-600">₹{p.amount}</span>
                          <span className="text-slate-400 ml-2 text-xs">{formatDate(p.payment_date)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Collect Receipt Dialog - with auto Daily Hisab */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Collect Receipt - {guestDetails?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRecordPayment} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Guest</Label>
                <Input value={`${guestDetails?.name} - ${guestDetails?.room_info || ''} (${guestDetails?.status?.replace('_', ' ')})`} disabled className="bg-slate-50" />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input 
                  type="date" 
                  value={paymentForm.payment_date} 
                  onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })} 
                  data-testid="payment-date"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <span className="text-emerald-600">💵</span>
                Cash Receipt
              </h4>
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-2">
                  <Label>Rent (₹)</Label>
                  <Input type="number" min="0" value={paymentForm.cash_rent} onChange={(e) => setPaymentForm({ ...paymentForm, cash_rent: parseFloat(e.target.value) || 0 })} data-testid="payment-cash-rent" />
                </div>
                <div className="space-y-2">
                  <Label>Deposit (₹)</Label>
                  <Input type="number" min="0" value={paymentForm.cash_deposit} onChange={(e) => setPaymentForm({ ...paymentForm, cash_deposit: parseFloat(e.target.value) || 0 })} data-testid="payment-cash-deposit" />
                </div>
                <div className="space-y-2">
                  <Label>Electricity (₹)</Label>
                  <Input type="number" min="0" value={paymentForm.cash_electricity} onChange={(e) => setPaymentForm({ ...paymentForm, cash_electricity: parseFloat(e.target.value) || 0 })} data-testid="payment-cash-electricity" />
                </div>
                {!(guestDetails?.status === 'day_basis' || guestDetails?.booking_type === 'day_basis') && (
                  <div className="space-y-2">
                    <Label>Penalty (₹)</Label>
                    <Input type="number" min="0" value={paymentForm.cash_penalty} onChange={(e) => setPaymentForm({ ...paymentForm, cash_penalty: parseFloat(e.target.value) || 0 })} data-testid="payment-cash-penalty" />
                  </div>
                )}
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <span className="text-blue-600">🏦</span>
                Bank Receipt
              </h4>
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-2">
                  <Label>Rent (₹)</Label>
                  <Input type="number" min="0" value={paymentForm.bank_rent} onChange={(e) => setPaymentForm({ ...paymentForm, bank_rent: parseFloat(e.target.value) || 0 })} data-testid="payment-bank-rent" />
                </div>
                <div className="space-y-2">
                  <Label>Deposit (₹)</Label>
                  <Input type="number" min="0" value={paymentForm.bank_deposit} onChange={(e) => setPaymentForm({ ...paymentForm, bank_deposit: parseFloat(e.target.value) || 0 })} data-testid="payment-bank-deposit" />
                </div>
                <div className="space-y-2">
                  <Label>Electricity (₹)</Label>
                  <Input type="number" min="0" value={paymentForm.bank_electricity} onChange={(e) => setPaymentForm({ ...paymentForm, bank_electricity: parseFloat(e.target.value) || 0 })} data-testid="payment-bank-electricity" />
                </div>
                {!(guestDetails?.status === 'day_basis' || guestDetails?.booking_type === 'day_basis') && (
                  <div className="space-y-2">
                    <Label>Penalty (₹)</Label>
                    <Input type="number" min="0" value={paymentForm.bank_penalty} onChange={(e) => setPaymentForm({ ...paymentForm, bank_penalty: parseFloat(e.target.value) || 0 })} data-testid="payment-bank-penalty" />
                  </div>
                )}
              </div>
            </div>

            {/* ===== Penalty / Waiver box ===== */}
            <div className="border-t pt-4" data-testid="penalty-waiver-box">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-rose-600" />
                  Penalty / Waiver
                </h4>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-slate-500">Penalty Due:</span>
                  <span className="font-mono font-bold text-rose-600" data-testid="penalty-due-value">₹{(adjSummary.penalty_due || 0).toLocaleString('en-IN')}</span>
                </div>
              </div>

              <div className="flex gap-2 mb-3">
                {!(guestDetails?.status === 'day_basis' || guestDetails?.booking_type === 'day_basis') && (
                  <Button type="button" size="sm" variant="outline" onClick={handleStartAddPenalty} className="text-rose-600 border-rose-200" data-testid="add-penalty-btn">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add Penalty
                  </Button>
                )}
                <Button type="button" size="sm" variant="outline" onClick={() => { setShowPenaltyForm(false); setShowWaiverForm(true); }} className="text-violet-600 border-violet-200" data-testid="apply-waiver-btn">
                  <Gift className="h-3.5 w-3.5 mr-1" /> Apply Waiver
                </Button>
              </div>

              {showPenaltyForm && (
                <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3 mb-3 space-y-2" data-testid="penalty-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Penalty Amount (₹)</Label>
                      <Input type="number" min="0" value={penaltyForm.amount} onChange={(e) => setPenaltyForm({ ...penaltyForm, amount: parseFloat(e.target.value) || 0 })} data-testid="penalty-amount-input" />
                      <p className="text-[10px] text-slate-500">Suggested: {penaltyForm.days_charged} day(s) × ₹{penaltyForm.per_day_rate}. Editable.</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Month (optional)</Label>
                      <Input type="month" value={penaltyForm.month_year} onChange={(e) => setPenaltyForm({ ...penaltyForm, month_year: e.target.value })} data-testid="penalty-month-input" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Reason (optional)</Label>
                    <Input value={penaltyForm.reason} onChange={(e) => setPenaltyForm({ ...penaltyForm, reason: e.target.value })} placeholder="Reason / remark" data-testid="penalty-reason-input" />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => setShowPenaltyForm(false)} className="flex-1">Cancel</Button>
                    <Button type="button" size="sm" onClick={handleSavePenalty} className="flex-1 bg-rose-600 hover:bg-rose-700" data-testid="save-penalty-btn">Save Penalty</Button>
                  </div>
                </div>
              )}

              {showWaiverForm && (
                <div className="rounded-lg border border-violet-200 bg-violet-50/60 p-3 mb-3 space-y-2" data-testid="waiver-form">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Reduce From</Label>
                      <Select value={waiverForm.component} onValueChange={(v) => setWaiverForm({ ...waiverForm, component: v })}>
                        <SelectTrigger data-testid="waiver-component-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rent">Rent</SelectItem>
                          <SelectItem value="electricity">Electricity</SelectItem>
                          <SelectItem value="deposit">Deposit</SelectItem>
                          <SelectItem value="penalty">Penalty</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Waiver Amount (₹)</Label>
                      <Input type="number" min="0" value={waiverForm.amount} onChange={(e) => setWaiverForm({ ...waiverForm, amount: parseFloat(e.target.value) || 0 })} data-testid="waiver-amount-input" />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Reason (optional)</Label>
                    <Input value={waiverForm.reason} onChange={(e) => setWaiverForm({ ...waiverForm, reason: e.target.value })} placeholder="Reason / remark" data-testid="waiver-reason-input" />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="outline" onClick={() => setShowWaiverForm(false)} className="flex-1">Cancel</Button>
                    <Button type="button" size="sm" onClick={handleSaveWaiver} className="flex-1 bg-violet-600 hover:bg-violet-700" data-testid="save-waiver-btn">Apply Waiver</Button>
                  </div>
                </div>
              )}

              {adjustments.length > 0 && (
                <div className="space-y-1.5" data-testid="adjustments-list">
                  {adjustments.map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs" data-testid={`adjustment-row-${a.id}`}>
                      {editingAdjId === a.id ? (
                        <div className="flex items-center gap-2 w-full">
                          <Input type="number" min="0" value={editAdjForm.amount} onChange={(e) => setEditAdjForm({ ...editAdjForm, amount: parseFloat(e.target.value) || 0 })} className="h-7 w-24" data-testid="edit-adj-amount" />
                          <Input value={editAdjForm.reason} onChange={(e) => setEditAdjForm({ ...editAdjForm, reason: e.target.value })} className="h-7 flex-1" placeholder="Reason" />
                          <Button type="button" size="sm" className="h-7 bg-emerald-600" onClick={() => handleSaveEditAdjustment(a.id)} data-testid="save-edit-adj-btn"><Save className="h-3.5 w-3.5" /></Button>
                          <Button type="button" size="sm" variant="outline" className="h-7" onClick={() => setEditingAdjId(null)}><X className="h-3.5 w-3.5" /></Button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${a.adj_type === 'penalty' ? 'bg-rose-100 text-rose-700' : 'bg-violet-100 text-violet-700'}`}>
                              {a.adj_type === 'penalty' ? 'PENALTY' : `WAIVER · ${(a.component || '').toUpperCase()}`}
                            </span>
                            <span className="truncate text-slate-600">{a.reason || '—'}</span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="font-mono font-bold text-slate-700">
                              ₹{(a.amount || 0).toLocaleString('en-IN')}{a.adj_type === 'penalty' && a.amount_paid > 0 ? ` (paid ₹${a.amount_paid.toLocaleString('en-IN')})` : ''}
                            </span>
                            <button type="button" onClick={() => { setEditingAdjId(a.id); setEditAdjForm({ amount: a.amount, reason: a.reason || '' }); }} className="text-slate-400 hover:text-blue-600" data-testid={`edit-adj-${a.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                            <button type="button" onClick={() => handleDeleteAdjustment(a.id)} className="text-slate-400 hover:text-rose-600" data-testid={`delete-adj-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Remarks</Label>
              <Input value={paymentForm.remarks} onChange={(e) => setPaymentForm({ ...paymentForm, remarks: e.target.value.toUpperCase() })} placeholder="OPTIONAL REMARKS..." />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setPaymentDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="save-payment-btn">Record Receipt</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Advance Rent Dialog */}
      <Dialog open={advanceRentDialogOpen} onOpenChange={setAdvanceRentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-purple-600" />
              Advance Rent Payment - {guestDetails?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-4 bg-slate-50 rounded-lg text-sm">
              <div className="flex justify-between mb-2">
                <span className="text-slate-500">Monthly Rent:</span>
                <span className="font-mono font-bold">₹{(guestDetails?.monthly_rent || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Current Rent Due:</span>
                <span className="font-mono text-amber-600">₹{(guestDetails?.advance_rent_due || 0).toLocaleString()}</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Payment Date</Label>
              <Input type="date" value={advanceRentForm.payment_date} onChange={(e) => setAdvanceRentForm({ ...advanceRentForm, payment_date: e.target.value })} data-testid="advance-rent-date-input" />
            </div>
            
            <div className="space-y-2">
              <Label>Select Advance Period</Label>
              <div className="grid grid-cols-4 gap-2">
                {[2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                  <Button
                    key={m}
                    type="button"
                    variant={advanceRentForm.months === m ? "default" : "outline"}
                    className={advanceRentForm.months === m ? "bg-purple-600 hover:bg-purple-700" : ""}
                    onClick={() => setAdvanceRentForm({ ...advanceRentForm, months: m })}
                  >
                    {m} Months
                  </Button>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Payment Mode</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant={advanceRentForm.payment_mode === 'cash' ? "default" : "outline"}
                  className={advanceRentForm.payment_mode === 'cash' ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                  onClick={() => setAdvanceRentForm({ ...advanceRentForm, payment_mode: 'cash' })}
                >
                  💵 Cash
                </Button>
                <Button
                  type="button"
                  variant={advanceRentForm.payment_mode === 'bank' ? "default" : "outline"}
                  className={advanceRentForm.payment_mode === 'bank' ? "bg-blue-600 hover:bg-blue-700" : ""}
                  onClick={() => setAdvanceRentForm({ ...advanceRentForm, payment_mode: 'bank' })}
                >
                  🏦 Bank
                </Button>
              </div>
            </div>
            
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-purple-600">Total Amount</p>
                  <p className="text-2xl font-mono font-bold text-purple-700">
                    ₹{((guestDetails?.monthly_rent || 0) * advanceRentForm.months).toLocaleString()}
                  </p>
                </div>
                <div className="text-right text-sm text-purple-600">
                  <p>For {advanceRentForm.months} months</p>
                  <p>Auto-deducts monthly</p>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setAdvanceRentDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleAdvanceRent} className="flex-1 bg-purple-600 hover:bg-purple-700" data-testid="confirm-advance-rent-btn">
                Record Advance Rent
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settle Dues Dialog */}
      <Dialog open={settleDuesDialogOpen} onOpenChange={setSettleDuesDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-orange-600" />
              Settle Dues - {guestDetails?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Current Dues Summary */}
            <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
              <div className="font-semibold text-slate-700 mb-2">Outstanding Summary</div>
              <div className="flex justify-between">
                <span className="text-slate-500">Rent Due:</span>
                <span className="font-mono text-red-600">₹{(guestDetails?.rent_due || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Deposit Due:</span>
                <span className="font-mono text-red-600">₹{(guestDetails?.deposit_due || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Electricity Due:</span>
                <span className="font-mono text-red-600">₹{(guestDetails?.electricity_due || 0).toLocaleString()}</span>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">Deposit Paid:</span>
                  <span className="font-mono text-green-600">₹{(guestDetails?.deposit_paid || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Advance Balance:</span>
                  <span className="font-mono text-green-600">₹{(guestDetails?.advance_rent_balance || 0).toLocaleString()}</span>
                </div>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between font-bold">
                  <span className="text-slate-700">Net Balance:</span>
                  {(() => {
                    const totalDue = (guestDetails?.rent_due || 0) + (guestDetails?.deposit_due || 0) + (guestDetails?.electricity_due || 0);
                    const totalCredit = (guestDetails?.deposit_paid || 0) + (guestDetails?.advance_rent_balance || 0);
                    const netBalance = totalCredit - totalDue;
                    return (
                      <span className={`font-mono ${netBalance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {netBalance >= 0 ? '+' : '-'}₹{Math.abs(netBalance).toLocaleString()}
                        <span className="text-xs ml-1">({netBalance >= 0 ? 'Credit to Guest' : 'Due from Guest'})</span>
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200 text-sm text-orange-700">
              <p className="font-medium">What this does:</p>
              <p className="mt-1">Clicking "Settle & Write Off" will zero out all dues and credits for this guest. Use this for small nominal amounts that won't be collected or refunded.</p>
            </div>

            {/* Remarks */}
            <div className="space-y-2">
              <Label>Remarks (Optional)</Label>
              <Input 
                placeholder="E.G., SMALL AMOUNT WRITTEN OFF AS PER MUTUAL AGREEMENT"
                value={settleDuesForm.remarks}
                onChange={(e) => setSettleDuesForm({ ...settleDuesForm, remarks: e.target.value.toUpperCase() })}
                data-testid="settle-remarks-input"
              />
            </div>
            
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setSettleDuesDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSettleDues} className="flex-1 bg-orange-600 hover:bg-orange-700" data-testid="confirm-settle-btn">
                <CheckCircle className="h-4 w-4 mr-2" />
                Settle & Write Off
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notice Dialog */}
      <Dialog open={noticeDialogOpen} onOpenChange={setNoticeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Give Notice - {selectedGuest?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Notice Date (leave empty for today)</Label>
              <Input type="date" value={noticeDate} onChange={(e) => setNoticeDate(e.target.value)} data-testid="notice-date-input" />
            </div>
            
            {/* Advance Notice Toggle */}
            <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
              <input 
                type="checkbox" 
                id="advance_notice" 
                checked={useAdvanceNotice} 
                onChange={(e) => setUseAdvanceNotice(e.target.checked)}
                className="rounded border-amber-400"
                data-testid="advance-notice-checkbox"
              />
              <Label htmlFor="advance_notice" className="text-sm text-amber-700 cursor-pointer flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Advance Notice (set specific leave date)
              </Label>
            </div>
            
            {useAdvanceNotice ? (
              <div className="space-y-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Label className="text-blue-700">Expected Leave Date</Label>
                <Input 
                  type="date" 
                  value={expectedLeaveDate} 
                  onChange={(e) => setExpectedLeaveDate(e.target.value)} 
                  min={noticeDate || getISTDateString()}
                  data-testid="expected-leave-date-input" 
                />
                <p className="text-xs text-blue-600 mt-1">
                  Guest will be on notice until this date
                </p>
              </div>
            ) : (
              <p className="text-sm text-slate-500 p-2 bg-slate-50 rounded">
                Expected leave date will be calculated as <span className="font-medium">30 days</span> from notice date.
              </p>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => { setNoticeDialogOpen(false); setUseAdvanceNotice(false); setExpectedLeaveDate(''); }} className="flex-1">Cancel</Button>
              <Button onClick={handleGiveNotice} className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="confirm-notice-btn">Give Notice</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Leave Dialog */}
      <Dialog open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Process Leave - {selectedGuest?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Show deposit info */}
            {selectedGuest && (
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Deposit Paid:</span>
                  <span className="font-mono font-medium text-emerald-600">₹{(selectedGuest.deposit_paid || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-slate-600">Deposit Due:</span>
                  <span className="font-mono font-medium text-rose-600">₹{(selectedGuest.deposit_due || 0).toLocaleString()}</span>
                </div>
              </div>
            )}
            
            {/* Day Basis Guest Info */}
            {selectedGuest?.status === 'day_basis' && (
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm">
                <div className="flex justify-between">
                  <span className="text-purple-600">Check-in Date:</span>
                  <span className="font-mono font-medium">{formatDate(selectedGuest.joining_date)}</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-purple-600">Number of Days:</span>
                  <span className="font-mono font-medium">{selectedGuest.effective_days || selectedGuest.num_days || 1} days</span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-purple-600">Expected Checkout:</span>
                  <span className="font-mono font-medium">
                    {selectedGuest.effective_checkout || (selectedGuest.joining_date && (() => {
                      const joinDate = new Date(selectedGuest.joining_date);
                      joinDate.setDate(joinDate.getDate() + (selectedGuest.num_days || 1));
                      return joinDate.toISOString().split('T')[0];
                    })())}
                  </span>
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-purple-600">Daily Rate:</span>
                  <span className="font-mono font-medium text-purple-700">₹{(selectedGuest.daily_rate || 0).toLocaleString()}/day</span>
                </div>
                
                {/* Calculate and show overstay if leave date is after EFFECTIVE (paid) checkout */}
                {leaveForm.leave_date && selectedGuest.joining_date && (() => {
                  let checkoutDate;
                  if (selectedGuest.effective_checkout) {
                    checkoutDate = new Date(selectedGuest.effective_checkout);
                  } else {
                    const joinDate = new Date(selectedGuest.joining_date);
                    checkoutDate = new Date(joinDate);
                    checkoutDate.setDate(checkoutDate.getDate() + (selectedGuest.num_days || 1));
                  }
                  const leaveDate = new Date(leaveForm.leave_date);
                  
                  if (leaveDate > checkoutDate) {
                    const overstayDays = Math.ceil((leaveDate - checkoutDate) / (1000 * 60 * 60 * 24));
                    const overstayRent = overstayDays * (selectedGuest.daily_rate || 0);
                    return (
                      <div className="mt-3 pt-3 border-t border-purple-200">
                        <div className="flex justify-between text-rose-600">
                          <span className="font-medium">⚠️ Overstay Days:</span>
                          <span className="font-mono font-bold">{overstayDays} days</span>
                        </div>
                        <div className="flex justify-between text-rose-700 mt-1">
                          <span className="font-medium">Overstay Rent Due:</span>
                          <span className="font-mono font-bold">₹{overstayRent.toLocaleString()}</span>
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <div className="mt-3 pt-3 border-t border-purple-200">
                        <div className="flex justify-between text-emerald-600">
                          <span className="font-medium">✓ No Overstay</span>
                          <span className="font-mono">Leaving on/before checkout</span>
                        </div>
                      </div>
                    );
                  }
                })()}
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Leave Date</Label>
              <Input type="date" value={leaveForm.leave_date} onChange={(e) => setLeaveForm({ ...leaveForm, leave_date: e.target.value })} data-testid="leave-date-input" />
            </div>
            {selectedGuest?.status !== 'notice' && (
              <>
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="immediate" 
                    checked={leaveForm.immediate} 
                    onChange={(e) => setLeaveForm({ ...leaveForm, immediate: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="immediate">Immediate Leave (without notice period)</Label>
                </div>
                {leaveForm.immediate && (
                  <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg">
                    <p className="text-sm text-rose-700 font-medium">⚠️ Deposit & Advance Rent will be forfeited</p>
                    <p className="text-xs text-rose-600 mt-1">Net Balance will be set to ₹0 (account closes with zero balance)</p>
                  </div>
                )}
              </>
            )}
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setLeaveDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleProcessLeave} className="flex-1 bg-rose-600 hover:bg-rose-700" data-testid="confirm-leave-btn">Process Leave</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Shift Dialog */}
      <Dialog open={shiftDialogOpen} onOpenChange={setShiftDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Shift Guest - {selectedGuest?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Current Room Info */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Current Room</p>
              <p className="font-medium">{selectedGuest?.room_info} - ₹{selectedGuest?.monthly_rent}/month</p>
            </div>
            
            <div className="space-y-2">
              <Label>Shift Date</Label>
              <Input type="date" value={shiftForm.shift_date} onChange={(e) => setShiftForm({ ...shiftForm, shift_date: e.target.value })} data-testid="shift-date-input" />
            </div>
            <div className="space-y-2">
              <Label>New Room</Label>
              <Select value={shiftForm.new_room_id} onValueChange={(val) => setShiftForm({ ...shiftForm, new_room_id: val })}>
                <SelectTrigger data-testid="shift-room-select"><SelectValue placeholder="Select room" /></SelectTrigger>
                <SelectContent>
                  {shiftFilteredRooms.map(r => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.combined_number} ({r.property_name}) - ₹{r.rent_per_bed}/month - {r.beds?.filter(b => b.status === 'vacant').length} vacant
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>New Bed</Label>
              <Select value={shiftForm.new_bed_no.toString()} onValueChange={(val) => setShiftForm({ ...shiftForm, new_bed_no: parseInt(val) })}>
                <SelectTrigger data-testid="shift-bed-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {shiftForm.new_room_id && rooms.find(r => r.id === shiftForm.new_room_id)?.beds
                    ?.filter(b => b.status === 'vacant')
                    .map(b => <SelectItem key={b.bed_no} value={b.bed_no.toString()}>Bed {b.bed_no}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            
            {/* Pro-rated Adjustment Preview */}
            {shiftForm.new_room_id && (() => {
              const newRoom = rooms.find(r => r.id === shiftForm.new_room_id);
              const oldRent = selectedGuest?.monthly_rent || 0;
              const newRent = newRoom?.rent_per_bed || 0;
              const shiftDate = shiftForm.shift_date ? new Date(shiftForm.shift_date + 'T00:00:00') : new Date();
              const STANDARD_MONTH_DAYS = 30;
              
              // Calculate days in old room: from max(joining_date, month_start) to shift_date
              const monthStart = new Date(shiftDate.getFullYear(), shiftDate.getMonth(), 1);
              const joiningDate = selectedGuest?.joining_date ? new Date(selectedGuest.joining_date + 'T00:00:00') : monthStart;
              const effectiveStart = joiningDate > monthStart ? joiningDate : monthStart;
              const daysInOldRoom = Math.max(0, Math.floor((shiftDate - effectiveStart) / (1000 * 60 * 60 * 24)));
              
              // adjustment = daysInOldRoom × (oldRent - newRent) / 30
              const proratedAdjustment = daysInOldRoom > 0 ? Math.round((daysInOldRoom * (oldRent - newRent)) / STANDARD_MONTH_DAYS) : 0;
              
              if (oldRent !== newRent) {
                const isUpgrade = newRent > oldRent;
                return (
                  <div className={`p-3 rounded-lg border ${isUpgrade ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                    <p className="text-sm font-medium mb-1">
                      {isUpgrade ? 'Upgrade' : 'Downgrade'} Adjustment
                    </p>
                    <p className="text-sm text-gray-600">
                      Old rent: ₹{oldRent.toLocaleString()} → New rent: ₹{newRent.toLocaleString()}
                    </p>
                    <p className="text-sm text-gray-600">
                      Days in old room this month: {daysInOldRoom}
                    </p>
                    {proratedAdjustment !== 0 ? (
                      <p className={`font-semibold mt-1 ${proratedAdjustment > 0 ? 'text-orange-700' : 'text-green-700'}`}>
                        {proratedAdjustment > 0 ? `Extra charge: +₹${proratedAdjustment.toLocaleString()}` : `Credit: ₹${Math.abs(proratedAdjustment).toLocaleString()}`}
                      </p>
                    ) : (
                      <p className="font-semibold mt-1 text-slate-700">No adjustment (0 days in old room)</p>
                    )}
                  </div>
                );
              }
              return null;
            })()}
            
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShiftDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleShift} className="flex-1 bg-purple-600 hover:bg-purple-700" data-testid="confirm-shift-btn">Shift Guest</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Convert to Joined Dialog (for booked guests) */}
      <Dialog open={convertJoinedDialogOpen} onOpenChange={setConvertJoinedDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-emerald-700">Convert to Joined - {selectedGuest?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-500">
              This will convert the booked guest to joined status. The bed will be marked as occupied.
            </p>
            <div className="space-y-2">
              <Label>Joining Date</Label>
              <Input 
                type="date" 
                value={convertJoinedForm.joining_date} 
                onChange={(e) => setConvertJoinedForm({ ...convertJoinedForm, joining_date: e.target.value })} 
                required
                data-testid="convert-joining-date-input" 
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setConvertJoinedDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleConvertToJoined} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-convert-btn">
                <Users className="h-4 w-4 mr-2" />
                Convert to Joined
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cancel Booking Dialog (for booked guests) */}
      <Dialog open={cancelBookingDialogOpen} onOpenChange={setCancelBookingDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-rose-700">Cancel Booking - {selectedGuest?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <p className="text-sm text-slate-500">
              This will cancel the booking and free up the bed.
            </p>
            <div className="space-y-2">
              <Label>Cancellation Date</Label>
              <Input 
                type="date" 
                value={cancelBookingForm.cancellation_date} 
                onChange={(e) => setCancelBookingForm({ ...cancelBookingForm, cancellation_date: e.target.value })} 
                required
                data-testid="cancellation-date-input" 
              />
            </div>
            <div className="space-y-2">
              <Label>Reason for Cancellation</Label>
              <Input 
                value={cancelBookingForm.reason} 
                onChange={(e) => setCancelBookingForm({ ...cancelBookingForm, reason: e.target.value })} 
                placeholder="Enter reason for cancellation..."
                data-testid="cancellation-reason-input" 
              />
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setCancelBookingDialogOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleCancelBooking} className="flex-1 bg-rose-600 hover:bg-rose-700" data-testid="confirm-cancel-booking-btn">
                <X className="h-4 w-4 mr-2" />
                Cancel Booking
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};
